/**
 * Detect in-app browsers (TikTok, Instagram, Facebook, Snapchat, Twitter, Line).
 *
 * These browsers break Stripe 3D Secure redirects because they intercept
 * window.open / location.href changes. We warn users to open in their
 * system browser (Safari / Chrome) before initiating payment.
 *
 * Ported from the Angular codebase:
 * favotrip_web/frontend/src/app/layout/voucher-checkout/voucher-checkout.component.ts:370-381
 */

const IN_APP_PATTERN = /FBAN|FBAV|Instagram|TikTok|musical_ly|BytedanceWebview|Snapchat|Twitter|Line\//i;

/** True if the UA is an in-app browser (TikTok/Instagram/Facebook/Snapchat/...). These break Stripe 3D Secure redirects, so the UI warns the user to open in their system browser before paying. */
export function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return IN_APP_PATTERN.test(ua);
}

/**
 * Detect specifically the TikTok in-app browser.
 */
export function isTikTokBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /TikTok|musical_ly|BytedanceWebview/i.test(ua);
}

/**
 * Get the name of the detected in-app browser for display purposes.
 */
export function getInAppBrowserName() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/TikTok|musical_ly|BytedanceWebview/i.test(ua)) return "TikTok";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/FBAN|FBAV/i.test(ua)) return "Facebook";
  if (/Snapchat/i.test(ua)) return "Snapchat";
  if (/Twitter/i.test(ua)) return "Twitter";
  if (/Line\//i.test(ua)) return "Line";
  return null;
}

/**
 * Attempt to open the current page in the system browser.
 * Uses multiple strategies because in-app browsers handle this differently:
 * 1. intent:// scheme for Android
 * 2. x-safari-https:// for iOS Safari
 * 3. window.open with _system target
 * 4. Fallback: show copy-URL instruction
 */
export function openInSystemBrowser(url) {
  const targetUrl = url || window.location.href;

  // Try window.open with _system first (works in some WebViews)
  const win = window.open(targetUrl, "_system");
  if (win) return;

  // Try Android intent scheme
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) {
    window.location.href = `intent://${targetUrl.replace(/^https?:\/\//, "")}#Intent;scheme=https;end`;
    return;
  }

  // Fallback: just navigate (some in-app browsers will open external)
  window.location.href = targetUrl;
}
